<template>
	<v-row class="full-height" style="position: relative;">
		<v-overlay :value="loadingTableData" absolute opacity="0.15">
			<v-progress-circular indeterminate size="48" color="primary"></v-progress-circular>
		</v-overlay>
		<v-col cols="12" class="flex-none receiving-actions-col" :class="{ 'is-stuck': isStuck }">
			<div class="receiving-header">
				<div class="receiving-header-actions">
					<span v-if="selectedCount > 0" class="receiving-selection-chip">
						<b>{{ selectedCount }}</b> line{{ selectedCount === 1 ? "" : "s" }} selected
					</span>
					<!-- Jump to the order the PO belongs to (#1128). The icon link beside the PO # stays;
					     this labelled button in the sticky actions bar is the one people find. -->
					<v-btn
						v-if="isValidPurchaseOrder && receivingData.salesOrderId"
						depressed
						outlined
						:to="{ path: `/sales-orders/orders/${receivingData.salesOrderId}` }"
						data-test="receiving-go-to-order"
					>
						<v-icon left small>mdi-open-in-new</v-icon>
						Go to Order
					</v-btn>
					<v-btn depressed outlined @click="$router.push('/receiving/receiving-entry')"
						>Close</v-btn
					>
					<v-btn
						depressed
						outlined
						@click="
							() => {
								receiveAll = !receiveAll;
								if (receiveAll === false) {
									init();
								}
							}
						"
						:disabled="!canReceive"
						>{{ receiveAll ? "Deselect All" : "Receive All" }}</v-btn
					>
					<v-btn
						depressed
						color="success"
						@click="handleSubmit"
						:disabled="!canReceive || !hasSelection || saving"
						:loading="saving"
					>
						Save{{ selectedCount > 0 ? ` (${selectedCount})` : "" }}
					</v-btn>
				</div>
			</div>
		</v-col>
		<v-col cols="12" class="flex-none">
			<div class="receiving-form">
				<v-row>
					<v-col class="d-flex align-end">
						<div class="form-input" :class="receivingData.id ? 'd-inline-flex' : null">
							<div>
								<label>Purchase Order #</label>
								<template v-if="isValidPurchaseOrder">
									<v-tooltip top>
										<template v-slot:activator="{ on }">
											<router-link
												:to="{
													path: `/sales-orders/orders/${receivingData.salesOrderId}`,
												}"
												><v-icon small class="to-order ml-2" v-on="on">
													mdi-open-in-new
												</v-icon></router-link
											>
										</template>
										Go to Order
									</v-tooltip>
								</template>
							</div>

							<v-combobox
								prepend-inner-icon="mdi-magnify"
								dense
								outlined
								hide-details="auto"
								v-model="navigatedPurchaseOrderNumber"
								:items="poResults"
								item-text="orderNumber"
								item-value="orderNumber"
								@change="setPurchaseOrder"
								@keyup="searchPurchaseOrders($event.srcElement.value)"
							></v-combobox>
						</div>

						<StatusBadge
							v-if="receivingData.id && receivingData.orderStatus"
							:value="receivingData.orderStatus"
						/>
					</v-col>

					<v-col>
						<div class="form-input">
							<label>Vendor</label>
							<v-text-field
								outlined
								dense
								hide-details="auto"
								:value="receivingData.vendor"
								disabled
							></v-text-field>
						</div>
					</v-col>

					<v-col>
						<div class="form-input">
							<label>Date Received</label>
							<v-menu
								v-model="showReceivedDate"
								:close-on-content-click="false"
								transition="scale-transition"
								offset-y
								min-width="290px"
							>
								<template v-slot:activator="{ on, attrs }">
									<v-text-field
										:value="receivingData.receivedDate"
										append-icon="mdi-calendar"
										@click:append="showReceivedDate = true"
										outlined
										dense
										v-bind="attrs"
										v-on="on"
										disabled
										hide-details="auto"
									></v-text-field>
								</template>
								<v-date-picker
									v-model="receivingData.receivedDate"
									@input="showReceivedDate = false"
								></v-date-picker>
							</v-menu>
						</div>
					</v-col>
				</v-row>
			</div>
			<v-alert v-if="error" dense type="error" dismissible class="mt-2 mb-0">
				{{ error }}
			</v-alert>
		</v-col>

		<v-col cols="12" class="flex-auto" v-if="!relatedOrders || relatedOrders.length === 0">
			<div
				:class="[
					receivingData.purchaseOrderNumber == navigatedPurchaseOrderNumber ? 'table-highlight' : '',
					receivingData.isCancelled ? 'po-cancelled' : '',
				]"
			>
				<div class="receiving-related-heading">
					<div class="receiving-related-heading-info">
						<label v-if="receivingData.dropShip"> Dropship Order #: </label>
						<label v-else> Purchase Order #: </label>
						{{ receivingData.purchaseOrderNumber }}
						<template v-if="receivingData.purchaseOrderNumber">
							<v-tooltip bottom>
								<template v-slot:activator="{ on }">
									<router-link
										:to="{
											path: `/purchasing/purchase-orders/${receivingData.id}`,
										}"
										><v-icon small class="to-order" v-on="on">
											mdi-open-in-new
										</v-icon></router-link
									>
								</template>
								Go to Purchase Order
							</v-tooltip>
						</template>
					</div>
					<div class="receiving-related-heading-info">
						<label> Process Type: </label>
						<span style="text-transform: uppercase">{{
							receivingData.isAttaching
								? "Attach"
								: receivingData.processType
								? receivingData.processType.name
								: "Standard"
						}}</span>
					</div>
					<div class="receiving-related-heading-info">
						<label> Vendor: </label>
						{{ receivingData.vendor }}
					</div>
					<v-spacer></v-spacer>
					<v-btn
						v-if="
							!receivingData.dropShip &&
							canReceive &&
							!receivingData.isCancelled &&
							hasReceivedItems(receivingData)
						"
						depressed
						outlined
						class="unreceive-all-btn mr-3"
						@click="unreceiveAllMain"
					>
						<v-icon left small>mdi-undo-variant</v-icon>
						Unreceive All
					</v-btn>
					<div>
						<StatusBadge
							class="status-badge"
							v-if="receivingData.orderStatus"
							:value="receivingData.orderStatus"
						/>
					</div>
				</div>
				<DropshipEntryTable
					v-if="receivingData.dropShip && receivingData.purchaseOrderNumber"
					:key="updateKey"
					:updateKey="updateKey + receivingData.id"
					v-model="receivingData.items"
					:parent="receivingData.parentItem"
					:selectAll="receiveAll"
					:canReceive="canReceive && !receivingData.isCancelled"
				/>
				<ReceivingEntryTable
					v-else-if="receivingData.purchaseOrderNumber"
					ref="mainEntryTable"
					:key="updateKey"
					v-model="receivingData.items"
					:parent="receivingData.parentItem"
					:selectAll="receiveAll"
					:canReceive="canReceive && !receivingData.isCancelled"
				/>
			</div>
		</v-col>

		<v-col
			v-for="(order, i) in relatedOrders"
			:key="i"
			cols="12"
			:class="[
				i === relatedOrders.length - 1 ? 'flex-auto' : 'flex-none',
				order.parentItem.parentPoId ? 'sub-item' : '',
			]"
		>
			<div
				:class="[
					order.purchaseOrderNumber == navigatedPurchaseOrderNumber ? 'table-highlight' : '',
					order.isCancelled ? 'po-cancelled' : '',
				]"
			>
				<div class="receiving-related-heading">
					<div class="receiving-related-heading-info">
						<label v-if="order.dropShip"> Dropship Order #: </label>
						<label v-else> Purchase Order #: </label>
						{{ order.purchaseOrderNumber }}
						<template v-if="order.purchaseOrderNumber">
							<v-tooltip bottom>
								<template v-slot:activator="{ on }">
									<router-link
										:to="{
											path: `/purchasing/purchase-orders/${order.id}`,
										}"
										><v-icon small class="to-order" v-on="on">
											mdi-open-in-new
										</v-icon></router-link
									>
								</template>
								Go to Purchase Order
							</v-tooltip>
						</template>
					</div>
					<div class="receiving-related-heading-info">
						<label> Process Type: </label>
						<span style="text-transform: uppercase">
							{{
								order.isAttaching
									? "Attach"
									: order.processType
									? order.processType.name
									: "Standard"
							}}
						</span>
					</div>
					<div class="receiving-related-heading-info">
						<label> Vendor: </label>
						{{ order.vendor }}
					</div>
					<v-spacer></v-spacer>
					<v-btn
						v-if="!order.dropShip && canReceive && !order.isCancelled && hasReceivedItems(order)"
						depressed
						outlined
						class="unreceive-all-btn mr-3"
						@click="unreceiveAllRelated(order)"
					>
						<v-icon left small>mdi-undo-variant</v-icon>
						Unreceive All
					</v-btn>
					<div>
						<StatusBadge class="status-badge" v-if="order.orderStatus" :value="order.orderStatus" />
					</div>
				</div>

				<DropshipEntryTable
					v-if="order.dropShip && order.purchaseOrderNumber"
					:key="updateRelatedKey + order.id"
					:updateKey="updateRelatedKey + order.id"
					v-model="order.items"
					:parent="order.parentItem"
					:selectAll="receiveAll"
					:canReceive="canReceive && !order.isCancelled"
				/>
				<ReceivingEntryTable
					v-else-if="order.purchaseOrderNumber"
					ref="relatedEntryTables"
					:data-order-id="order.id"
					:key="updateRelatedKey + order.id"
					v-model="relatedOrders[i].items"
					:parent="relatedOrders[i].parentItem"
					:selectAll="receiveAll"
					:canReceive="canReceive && !order.isCancelled"
				/>
			</div>
		</v-col>

		<v-col cols="12" class="flex-none d-flex">
			<div class="receiving-notes">
				<div class="receiving-notes-heading">
					<h2>Order Notes</h2>
					<template v-if="showNoteDropdown">
						<v-btn depressed outlined @click="showNoteDropdown = false">Cancel</v-btn>
						<v-btn depressed outlined @click="saveNote()">Save</v-btn>
					</template>
					<v-btn v-else depressed outlined @click="newNote()">New</v-btn>
				</div>
				<div
					class="receiving-notes-dropdown"
					:class="showNoteDropdown ? 'receiving-notes-dropdown-active' : null"
				>
					<div class="form-textarea">
						<label>Message</label>
						<v-textarea
							outlined
							hide-details="auto"
							rows="12"
							v-model="noteData.noteText"
						></v-textarea>
					</div>
					<div class="receiving-notes-dropdown-author">
						<h3>Added By:</h3>
						<p>{{ noteData.author }}</p>
					</div>
				</div>
				<div class="receiving-notes-container">
					<div class="receiving-notes-container-list">
						<div
							v-for="note in receivingData.notes"
							:key="note.noteId"
							class="receiving-notes-container-note"
						>
							<span
								class="receiving-notes-container-note-type"
								:class="note.noteType == 'USER' ? 'receiving-notes-container-note-type-2' : null"
								>{{ note.noteType == "USER" ? "User Note" : "System Note" }}</span
							>
							<p>{{ note.noteText }}</p>
							<div class="receiving-notes-container-note-author">
								{{ formatDateLocal(note.createdAt) }} - {{ note.createdByUser }}
							</div>
						</div>
					</div>
				</div>
			</div>
		</v-col>

		<v-slide-y-reverse-transition>
			<div v-if="selectedCount > 0 && canReceive" class="receiving-selection-bar">
				<span class="receiving-selection-bar-count">
					<b>{{ selectedCount }}</b> line{{ selectedCount === 1 ? "" : "s" }} selected
				</span>
				<v-spacer></v-spacer>
				<v-btn depressed outlined @click="clearSelection">Clear Selection</v-btn>
				<v-btn
					depressed
					color="success"
					class="ml-2"
					@click="handleSubmit"
					:disabled="saving"
					:loading="saving"
				>
					Save ({{ selectedCount }})
				</v-btn>
			</div>
		</v-slide-y-reverse-transition>

		<WarningModal
			:showModal="warningModal"
			:message="warningMessage"
			:confirmText="'Okay'"
			:onlyShowConfirmOption="true"
			@cancel="warningModal = false"
			@confirm="warningModal = false"
		/>
	</v-row>
</template>

<script>
import gql from "graphql-tag";
import { dropShipChanged as itemDropShipChanged } from "@/helpers/dropShipChanges";
import DateFormatting from "@/mixins/DateFormatting";
import ReceivingEntryTable from "@/components/Receiving/ReceivingEntryTable.vue";
import DropshipEntryTable from "@/components/Receiving/DropshipEntryTable.vue";
import {
	SEARCH_PURCHASE_ORDERS,
	SEARCH_PURCHASE_ORDER_ITEMS,
	RECEIVED_ORDER_BY_ID,
	PURCHASE_ORDERS,
} from "@/gql/queries";
import StatusBadge from "../StatusBadge.vue";
import WarningModal from "@/components/WarningModal.vue";
import { hasPermission, SystemPermissions } from "@/models/User";

export default {
	name: "ReceivingView",
	mixins: [DateFormatting],
	props: {
		purchaseOrderId: {
			type: Number,
			required: false,
		},
	},
	components: {
		ReceivingEntryTable,
		DropshipEntryTable,
		StatusBadge,
		WarningModal,
	},
	data() {
		return {
			loadingTableData: false,
			saving: false,
			poResults: [],
			showReceivedDate: false,
			navigatedPurchaseOrderNumber: null,
			receivingData: {
				id: null,
				purchaseOrderNumber: this.purchaseOrderNumber,
				vendorId: null,
				vendor: null,
				orderStatus: null,
				receivedDate: "",
				items: [],
				notes: [],
			},
			relatedOrders: [],
			showNoteDropdown: false,
			noteData: {
				noteText: "",
				author: "",
			},
			error: "",
			updateKey: Math.random(),
			updateRelatedKey: Math.random(),
			receiveAll: false,
			warningModal: false,
			warningMessage: "",
			salesOrderId: null,
			isStuck: false,
		};
	},
	methods: {
		async getUserName(id) {
			try {
				const response = await this.$apollo.query({
					query: gql`
						query GetUserName($userId: Int!) {
							userNameById(userId: $userId) {
								fullName
								username
							}
						}
					`,
					variables: {
						userId: id,
					},
					fetchPolicy: "no-cache",
				});

				const user = response.data.userNameById;
				return user?.username || user?.fullName || "...";
			} catch (error) {
				console.error("Error fetching user:", error);
				return "...";
			}
		},
		newNote() {
			this.noteData = {
				noteId: null,
				noteType: "USER",
				noteDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
					.toISOString()
					.substr(0, 10),
				noteText: "",
				author: this.$store.getters.userInfo.username || this.$store.getters.userInfo.name,
				createdByUser: this.$store.getters.userInfo.username || this.$store.getters.userInfo.name,
			};
			this.showNoteDropdown = true;
		},
		async saveNote() {
			//add current active note to quote notes
			this.receivingData.notes.push({
				...this.noteData,
				noteId: +new Date(),
				noteType: "USER",
				createdBy: this.$store.getters.userInfo.userId,
				createdAt: `${new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
					.toISOString()
					.substr(0, 10)} ${new Date().toLocaleTimeString()}`,
			});

			this.showNoteDropdown = false;
			await this.submitNotes();
		},

		async submitNotes() {
			try {
				if (!this.receivingData.notes.length) return;

				let notes = this.receivingData.notes.map((note) => {
					let noteData = {
						text: note.noteText,
					};

					return noteData;
				});

				const res = await this.$http.put(
					`/api/purchasing/orders/${this.purchaseOrderId}/notes`,
					notes
				);
				console.log(res);
			} catch (error) {
				console.log(error);
			}
		},

		setPurchaseOrder($ev) {
			this.receivingData.receivedDate = "";
			this.relatedOrders = [];
			this.error = "";
			if ($ev) {
				this.receivingData.id = $ev.purchaseOrderId;
				this.receivingData.vendor = $ev.vendor;
				this.receivingData.orderStatus = $ev.orderStatus;

				//load the items associated with the selected purchase order?
				this.getPurchaseOrderInfo($ev.purchaseOrderId);
			} else {
				this.receivingData.id = "";
				this.receivingData.vendor = "";
				this.receivingData.orderStatus = "";
			}
		},

		async searchPurchaseOrders(input) {
			const res = await this.$apollo.query({
				query: SEARCH_PURCHASE_ORDERS,
				variables: {
					searchTerm: input,
					offset: 0,
					take: 500,
				},
			});
			this.poResults = res.data.purchaseOrderListings.items;
		},

		// PO line item sizes are persisted without ordering info (snapshot rows in
		// whatever order the PO was generated). Display them in the size chart's
		// configured sequence instead. Match by sizeId first, then by size name:
		// POs generated from a sales order carry SizeIds from the ORDER item's
		// chart, not the purchased item's chart resolved here — the two are
		// separate template copies that share names but not row IDs (same
		// approach as POPrintTemplate's buildSizeSequenceMap). Special sizes have
		// no sizeId and match by name only. Ties (lengths under one size) sort
		// numerically by length, then by lengthId.
		sortSizesByChartSequence(sizes, chartSizes) {
			if (!sizes || sizes.length < 2 || !chartSizes?.length) return sizes;
			const seqBySizeId = new Map();
			const seqByName = new Map();
			chartSizes.forEach((s) => {
				const seq = s.sequence ?? 1000;
				seqBySizeId.set(s.sizeId, seq);
				if (s.name) seqByName.set(s.name, seq);
			});
			const seqOf = (s) => seqBySizeId.get(s.sizeId) ?? seqByName.get(s.size) ?? 1000;
			return [...sizes].sort((a, b) => {
				const seqA = seqOf(a);
				const seqB = seqOf(b);
				if (seqA !== seqB) return seqA - seqB;
				const lenA = parseInt(a.length, 10);
				const lenB = parseInt(b.length, 10);
				if (!Number.isNaN(lenA) && !Number.isNaN(lenB) && lenA !== lenB) return lenA - lenB;
				return (a.lengthId ?? 0) - (b.lengthId ?? 0);
			});
		},

		refactorResponse(response) {
			return {
				...response,
				vendor: response.vendor.companyName,
				items: response.items.map((item, itemIndex) => {
					// Filter function to match received items to the correct PO line item
					// Uses itemIndex as the primary identifier since it uniquely identifies each item in a PO
					// ItemIndex works for both sales order-sourced POs and manual POs
					const matchesItem = (el) =>
						el.itemId === item.itemOrdered.inventoryItemId &&
						el.styleHash === item.styleHash &&
						el.itemIndex === itemIndex;

					const receivedNoSizes = response.receivedItems
						.filter(matchesItem)
						.reduce((sum, cur) => sum + cur.quantityReceived, 0);
					const receivedSizes = response.receivedItems
						.filter(matchesItem)
						.reduce(
							(acc, cur) => (cur.sizesReceived?.length > 0 ? [...acc, ...cur.sizesReceived] : acc),
							[]
						);
					const receivedSizesSpecial = response.receivedItems
						.filter(matchesItem)
						.reduce(
							(acc, cur) =>
								cur.specialSizesReceived?.length > 0 ? [...acc, ...cur.specialSizesReceived] : acc,
							[]
						);

					const sizes = item.sizes?.map((el) => {
						const quantityReceived = receivedSizes
							.filter(
								(receivedSize) =>
									receivedSize.sizeId === el.sizeId &&
									(!el.lengthId || receivedSize.lengthId === el.lengthId)
							)
							.reduce((sum, cur) => sum + cur.quantity, 0);
						return {
							...el,
							quantityReceived,
							receivedEditable: false,
							quantityToReceive: el.quantity - (quantityReceived || 0),
						};
					});
					const specialSizes = item.specialSizes?.map((el) => {
						const quantityReceived = receivedSizesSpecial
							.filter(
								(receivedSize) =>
									receivedSize.size === el.size &&
									receivedSize.length === el.length
							)
							.reduce((sum, cur) => sum + cur.quantity, 0);
						return {
							...el,
							quantityReceived,
							receivedEditable: false,
							quantityToReceive: el.quantity - (quantityReceived || 0),
						};
					});

					// Rank regular and special sizes TOGETHER so the entry tables can
					// interleave them smallest→largest. They must remain separate
					// arrays — the save payload and received-prior matching depend on
					// the distinction (regular sizes carry sizeId/lengthId, special
					// sizes are free-form name/length strings).
					this.sortSizesByChartSequence(
						[...(sizes || []), ...(specialSizes || [])],
						item.inventoryItem?.sizeChart?.sizes
					).forEach((el, i) => {
						el.displayOrder = i;
					});

					return {
						...item,
						itemIndex, // array index used to identify item in PO JSON
						salesOrderId: response.salesOrderId,
						selected: false, // for use to determine individual receiving
						receivedEditable: false, // for items w/ no size
						quantityReceived: receivedNoSizes || 0, // for items w/ no size
						quantityToReceive: item.quantity - (receivedNoSizes || 0), // for items w/ no size
						purchaseOrderId: response.id,
						systemCategoryId:
							this.$store.state.systemCategories.systemCategories.find(
								(el) => el.name === item.itemOrdered.systemCategory
							)?.systemCategoryId || 0,
						sizes,
						specialSizes,
					};
				}),
			};
		},

		// Related orders will have the same SalesOrderId
		async getRelatedPO(salesOrderId) {
			const { data } = await this.$apollo.query({
				query: PURCHASE_ORDERS,
				variables: {
					where: {
						salesOrderId: { eq: salesOrderId },
					},
				},
				fetchPolicy: "no-cache",
			});

			// Sort items: active first, cancelled last, then by lineNumber
			data.response.items.sort((a, b) => {
				if (a.isCancelled !== b.isCancelled) return a.isCancelled ? 1 : -1;
				return a.lineNumber - b.lineNumber;
			});

			this.relatedOrders = await Promise.all(
				data.response.items.map(async (item) => {
					let refactoredItem = this.refactorResponse(item);
					refactoredItem.orderStatus = await this.updateStatus(item.id);
					return refactoredItem;
				})
			);
		},

		determineOverallOrderStatus(items) {
			if (items.length === 0) return "Unknown";

			const statuses = new Set(items.map((item) => item.orderStatus));

			if (statuses.size === 1) {
				// If all items have the same status
				return items[0].orderStatus;
			}

			if (statuses.has("Received") || statuses.has("Shipped")) {
				// If any item is Received/Shipped and not all items are, it's Partially status
				return statuses.has("Shipped") ? "PartiallyShipped" : "PartiallyReceived";
			}

			if (statuses.has("PartiallyReceived")) return "PartiallyReceived";
			if (statuses.has("PartiallyShipped")) return "PartiallyShipped";
			if (statuses.has("OnOrder")) return "OnOrder";

			// If we can't determine a clear status, return the status of the first item
			return items[0].orderStatus;
		},

		async getPurchaseOrderInfo(purchaseOrderId) {
			this.loadingTableData = true;
			let po = await this.$apollo.query({
				fetchPolicy: "no-cache",
				query: RECEIVED_ORDER_BY_ID,
				variables: { id: purchaseOrderId },
			});
			let response = po.data.response;
			this.navigatedPurchaseOrderNumber = response.purchaseOrderNumber;

			// use the parent as mounting point if this is a subitem
			while (response && response.parentItem && response.parentItem.parentPoId) {
				po = await this.$apollo.query({
					fetchPolicy: "no-cache",
					query: RECEIVED_ORDER_BY_ID,
					variables: { id: response.parentItem.parentPoId },
				});

				response = po.data.response;
			}

			// Related orders
			if (response.salesOrderId) {
				await this.getRelatedPO(response.salesOrderId);
				this.updateRelatedKey = Math.random();
			}

			// Get user names for notes
			if (response.notes) {
				response.notes = await Promise.all(
					response.notes.map(async (note) => ({
						...note,
						createdByUser: await this.getUserName(note.createdBy),
					}))
				);
			}

			// receive date: latest date of inventory items
			const receivedDateArr = response.receivedItems.map((item) => item.receivedDate) || [];
			receivedDateArr.sort();
			const receivedDate = receivedDateArr.length
				? this.formatDate(receivedDateArr[receivedDateArr.length - 1])
				: "";

			this.receivingData = {
				...this.receivingData,
				...this.refactorResponse(response),
				receivedDate,
				salesOrderId: response.salesOrderId,
			};

			this.loadingTableData = false;
			this.updateKey = Math.random();

			let poItems = await this.$apollo.query({
				fetchPolicy: "no-cache",
				query: SEARCH_PURCHASE_ORDER_ITEMS,
				variables: { where: { purchaseOrderId: { eq: purchaseOrderId } } },
			});

			if (poItems.data && poItems.data.response && poItems.data.response.items) {
				const items = poItems.data.response.items;
				const overallStatus = this.determineOverallOrderStatus(items);
				this.receivingData.orderStatus = overallStatus;
			}

			this.scrollToHighlightedRecord();

			return;
		},

		async updateStatus(id = this.receivingData.id) {
			const res = await this.$apollo.query({
				query: SEARCH_PURCHASE_ORDERS,
				variables: {
					offset: 0,
					take: 500,
					where: {
						purchaseOrderId: {
							eq: id,
						},
					},
				},
				fetchPolicy: "no-cache",
			});
			const status = res.data.purchaseOrderListings.items?.[0]?.orderStatus;
			return status;
		},

		async handleSubmit() {
			// Set once anything is posted: from then on the page must reload even if a later
			// step fails, or the receives that did save stay on screen and get re-sent (#1493
			// follow-up: 503094-10S was received three times this way).
			let posted = false;
			try {
				this.saving = true;
				this.error = "";
				const requests = []; // Collect request data
				const dropShipRequests = []; // Collect dropship request data

				// Function to create the request body
				const createRequestBody = (item) => {
					const sizes =
						item.sizes?.map((el) => ({
							sizeId: el.sizeId,
							quantity: el.quantityToReceive,
							length: el.length,
							sizeLengthId: el.lengthId,
						})) ?? [];
					const specialSizes =
						item.specialSizes?.map((el) => ({
							size: el.size,
							quantity: el.quantityToReceive,
							length: el.length,
						})) ?? [];
					let quantity =
						sizes.reduce((sum, cur) => sum + cur.quantity, 0) +
						specialSizes.reduce((sum, cur) => sum + cur.quantity, 0);

					if (!sizes.length && !specialSizes.length) {
						quantity = item.quantityToReceive || 0;
					}

					return {
						purchaseOrderId: item.purchaseOrderId,
						inventoryItemId: item.itemOrdered.inventoryItemId,
						itemIndex: item.itemIndex,
						quantity,
						sizes,
						specialSizes,
						lineItemId: item.salesOrderLineItemId,
						styleHash: item.styleHash,
					};
				};

				// Process receivingData items (selected PO)
				let isParentUpdated = false;
				this.receivingData.items.forEach((item) => {
					if (item.selected) {
						requests.push(createRequestBody(item));
						isParentUpdated = true;
					} else if (this.receivingData.dropShip && this.dropShipChanged(item)) {
						dropShipRequests.push(item);
						isParentUpdated = true;
					}
				});

				// Process relatedOrders items (related POs)
				let isValid = true;
				this.relatedOrders.forEach((order) => {
					order.items.forEach((item) => {
						if (item.selected) {
							if (order.isAttaching) {
								let relatedOrder = this.relatedOrders.find(
									(ro) => !ro.isAttaching && ro.lineItemId === item.salesOrderLineItemId
								);
								if (relatedOrder) {
									let isRelatedItemSelected = relatedOrder.items.some((ri) => !ri.selected);
									if (relatedOrder.orderStatus !== "Received" && isRelatedItemSelected) {
										this.warningMessage = `You must receive in PO# ${relatedOrder.purchaseOrderNumber} before the attaching PO# ${order.purchaseOrderNumber}.`;
										this.warningModal = true;
										isValid = false;
									}
								}
							}

							if (isValid) {
								requests.push(createRequestBody(item));
							}
						} else if (order.dropShip && this.dropShipChanged(item)) {
							dropShipRequests.push(item);
						}
					});
				});

				if (!isValid) return;

				this.loadingTableData = true;

				if (requests.length) {
					// Make a single API call with the batch of requests
					posted = true;
					const response = await this.$http.post(`/api/receiving/batch`, requests);

					// Check for per-item failures in the batch response
					const failures = response.data?.filter((r) => r.status === "Failure") || [];
					if (failures.length) {
						const messages = [...new Set(failures.map((f) => f.message))].join("; ");
						this.error = messages;
						return;
					}
				}

				if (dropShipRequests.length) {
					posted = true;
					await this.shipBatch(dropShipRequests);
				}

				if (isParentUpdated) {
					let status = await this.updateStatus();
					this.receivingData.orderStatus = status;
				}
			} catch (error) {
				this.error = error.response?.data || "Something's wrong";
			} finally {
				if (posted) {
					try {
						await this.init();
					} catch (reloadError) {
						console.error("Failed to reload receiving data after save:", reloadError);
					}
				}
				this.saving = false;
				this.loadingTableData = false;
			}
		},

		// Save only sends drop-ship items whose ship records the user edited (helpers/dropShipChanges)
		dropShipChanged(item) {
			return itemDropShipChanged(item);
		},

		onWindowScroll() {
			// The actions bar leaves its natural position after ~24px of scroll
			// (container + column padding above it); past that it is pinned.
			this.isStuck = window.scrollY > 24;
		},

		clearSelection() {
			this.receivingData.items.forEach((item) => {
				item.selected = false;
			});
			this.relatedOrders.forEach((order) => {
				order.items.forEach((item) => {
					item.selected = false;
				});
			});
			this.receiveAll = false;
		},

		hasReceivedItems(order) {
			if (!order || !Array.isArray(order.items)) return false;
			return order.items.some((item) => {
				const sizes = [...(item.sizes || []), ...(item.specialSizes || [])];
				if (sizes.length === 0) return (item.quantityReceived || 0) > 0;
				return sizes.some((s) => (s.quantityReceived || 0) > 0);
			});
		},
		unreceiveAllMain() {
			const ref = this.$refs.mainEntryTable;
			if (ref?.unreceiveAll) ref.unreceiveAll();
		},
		unreceiveAllRelated(order) {
			const refs = this.$refs.relatedEntryTables;
			if (!refs) return;
			const list = Array.isArray(refs) ? refs : [refs];
			const target = list.find((c) => String(c?.$el?.dataset?.orderId) === String(order.id));
			if (target?.unreceiveAll) target.unreceiveAll();
		},

		async ship(item) {
			let dropShipRecords = [
				...item.sizes.flatMap((size) => size.dropShipRecords || []),
				...item.specialSizes.flatMap((size) => size.dropShipRecords || []),
			];

			if (dropShipRecords.length === 0) {
				dropShipRecords = item.dropShipRecords || [];
			}

			// Add styleHash to each dropShipRecord
			dropShipRecords = dropShipRecords.map((record) => ({
				...record,
				styleHash: item.styleHash,
			}));

			if (dropShipRecords.length > 0) {
				await this.$http.post(`/api/purchasing/orders/${item.salesOrderId || 0}/ship-dropship`, {
					dropShipRecords,
				});
			}
		},

		async shipBatch(items) {
			const allRecords = [];
			let salesOrderId = 0;

			for (const item of items) {
				let dropShipRecords = [
					...item.sizes.flatMap((size) => size.dropShipRecords || []),
					...item.specialSizes.flatMap((size) => size.dropShipRecords || []),
				];

				if (dropShipRecords.length === 0) {
					dropShipRecords = item.dropShipRecords || [];
				}

				dropShipRecords = dropShipRecords.map((record) => ({
					...record,
					styleHash: item.styleHash,
				}));

				allRecords.push(...dropShipRecords);
				if (item.salesOrderId) salesOrderId = item.salesOrderId;
			}

			if (allRecords.length > 0) {
				await this.$http.post(`/api/purchasing/orders/${salesOrderId}/ship-dropship`, {
					dropShipRecords: allRecords,
				});
			}
		},

		scrollToHighlightedRecord() {
			this.$nextTick(() => {
				const highlightedElement = document.querySelector(".table-highlight");
				if (highlightedElement) {
					highlightedElement.scrollIntoView({
						behavior: "smooth",
						block: "center",
					});
				}
			});
		},

		async init() {
			this.receivingData = {
				id: this.purchaseOrderId,
				purchaseOrderNumber: null,
				vendorId: null,
				vendor: null,
				orderStatus: null,
				isCancelled: false,
				receivedDate: "",
				items: [],
				notes: [],
				salesOrderId: null,
			};
			this.relatedOrders = [];

			if (this.purchaseOrderId) {
				await this.getPurchaseOrderInfo(this.purchaseOrderId);
			}
		},
	},
	computed: {
		canReceive() {
			const user = this.$store.getters.userInfo;
			if (hasPermission(user, SystemPermissions.Receiving_ReceivingEntry_Receive_UnReceive)) {
				return true;
			}
			return user.isAdmin;
		},
		selectedCount() {
			const countSelected = (items) => items.filter((item) => item.selected).length;
			return (
				countSelected(this.receivingData.items) +
				this.relatedOrders.reduce((sum, order) => sum + countSelected(order.items), 0)
			);
		},
		hasSelection() {
			// Check if any item in receivingData.items is selected
			const isSelectedInReceivingData =
				this.receivingData.items.some((item) => item.selected) || this.receivingData.dropShip;

			// Check if any item in relatedOrders is selected
			const isSelectedInRelatedOrders = this.relatedOrders.some(
				(order) => order.items.some((item) => item.selected) || order.dropShip
			);

			// Return true if any item is selected in either array
			return isSelectedInReceivingData || isSelectedInRelatedOrders;
		},
		isValidPurchaseOrder() {
			return (
				this.receivingData.purchaseOrderNumber &&
				!this.receivingData.purchaseOrderNumber.startsWith("7") &&
				!this.receivingData.purchaseOrderNumber.startsWith("9")
			);
		},
		orderNumberQuery() {
			if (this.receivingData.purchaseOrderNumber) {
				const parts = this.receivingData.purchaseOrderNumber.split("-");
				return parts[0];
			}
			return "";
		},
	},
	watch: {
		navigatedPurchaseOrderNumber() {
			this.scrollToHighlightedRecord();
		},
	},
	mounted() {
		this.init();
		window.addEventListener("scroll", this.onWindowScroll, { passive: true });
	},
	beforeDestroy() {
		window.removeEventListener("scroll", this.onWindowScroll);
	},
};
</script>

<style lang="scss" scoped>
.receiving-actions-col {
	position: sticky;
	top: 64px; // height of the fixed app bar
	z-index: 4; // above the entry tables' sticky column headers (z-index 1)

	// Full-bleed surface behind the bar when stuck. The column is centered in
	// the content area (v-main pads 256px for the nav drawer), so a centered
	// pseudo-element sized to the content area spans sidebar-to-edge even when
	// the container is narrower than the viewport on wide screens.
	&::before {
		content: "";
		position: absolute;
		top: 0;
		bottom: 0;
		left: 50%;
		transform: translateX(-50%);
		width: calc(100vw - 261px); // 256px nav drawer + 5px scrollbar
		background-color: $color-gray-light;
		border-bottom: 1px solid $color-gray;
		box-shadow: 0 4px 8px -4px rgba(0, 0, 0, 0.25);
		opacity: 0;
		transition: opacity 0.2s ease-in-out;
		z-index: -1;
	}

	&.is-stuck::before {
		opacity: 1;
	}
}

.receiving-selection-chip {
	display: inline-flex;
	align-items: center;
	align-self: center;
	font-size: 1.4rem;
	color: $color-black;
	background-color: $color-white;
	border: 1px solid $color-primary;
	border-radius: 25rem;
	padding: 0.4rem 1.2rem;
	margin-left: 0.8rem;
	white-space: nowrap;

	b {
		color: $color-primary;
		margin-right: 0.4rem;
	}
}

.receiving-selection-bar {
	position: fixed;
	bottom: 1.6rem;
	left: calc(50% + 128px); // visually centered over the content area (sidebar is 256px)
	transform: translateX(-50%);
	display: flex;
	align-items: center;
	width: 48rem;
	max-width: calc(100vw - 300px);
	padding: 1rem 2rem;
	background-color: $color-white;
	border: 1px solid $color-gray;
	border-radius: 8px;
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
	z-index: 5;

	&-count {
		font-size: 1.4rem;

		b {
			color: $color-primary;
		}
	}

	.v-btn {
		text-transform: initial;
	}
}

.receiving {
	&-header {
		display: flex;
		flex-flow: row wrap;
		align-items: center;

		&-actions {
			display: inline-flex;
			flex-flow: row wrap;
			margin-left: auto;

			.v-btn {
				background-color: $color-white;
				border-color: $color-gray;
				text-transform: initial;
				margin-left: 0.8rem;
			}
		}
	}

	&-form {
		position: relative;
		display: block;
		background-color: $color-white;
		border: 1px solid $color-gray;
		border-radius: 3px;
		padding: 0.5rem 1.5rem;

		.row {
			margin: initial;
		}

		.form-input {
			margin: initial;
		}
	}

	&-notes {
		position: relative;
		display: inline-flex;
		flex-flow: column;
		width: 50%;
		height: 100%;
		border: 1px solid $color-gray;
		background-color: $color-white;
		margin-right: 1rem;

		&-heading {
			display: flex;
			flex-flow: row nowrap;
			align-items: center;
			width: 100%;
			height: 4.7rem;
			background-color: $color-gray-light-2;
			border-bottom: 1px solid $color-gray;
			// padding: 0.5rem 1rem;
			z-index: 1;

			h2 {
				font-size: 1.4rem;
				font-weight: 700;
				text-transform: uppercase;
				margin-right: auto;
			}

			.v-btn {
				background-color: $color-white;

				&:nth-child(3) {
					margin-left: 0.8rem;
				}
			}
		}

		&-container {
			position: absolute;
			padding-top: 4.7rem; //match with heading height
			top: 0;
			bottom: 0;
			left: 0;
			right: 0;
			flex-grow: 1;
			background-color: $color-white;
			overflow-y: auto;

			&-list {
				display: flex;
				flex-flow: column nowrap;
				width: 100%;
			}

			&-note {
				display: flex;
				flex-flow: row nowrap;
				align-items: center;
				padding: 1rem 0.8rem;
				width: 100%;
				font-size: 1.4rem;
				border-bottom: 1px solid $color-gray;

				p {
					flex: 1 1 auto;
				}

				&-type {
					padding: 0.5rem 1rem;
					min-width: 12rem;
					text-align: center;
					border-radius: 25rem;
					background-color: $color-gray;
					font-weight: 700;
					font-size: 1.4rem;
					margin-right: 1rem;

					&-2 {
						background-color: $color-green;
					}
				}

				&-author {
					margin-left: 1rem;
				}
			}
		}

		&-dropdown {
			position: relative;
			display: block;
			background-color: $color-white;
			height: 0%;
			border-bottom: 1px solid transparent;
			overflow: hidden;
			transition: height 0.3s ease-in-out;
			z-index: 5;

			&-active {
				height: 100%;
				box-shadow: rgba(50, 50, 93, 0.05) 0px 2px 5px -1px, rgba(0, 0, 0, 0.2) 0px 1px 3px -1px !important;
			}

			.form-input,
			.form-textarea {
				margin: 1.25rem;
			}

			.form-input {
				display: inline-flex;
				width: calc(50% - 2.5rem);
			}

			.form-textarea {
				label {
					width: 19.5%;
				}
			}

			&-author {
				display: flex;
				align-items: center;
				justify-content: flex-end;
				font-size: 1.4rem;
				margin: 1.25rem;

				h3 {
					font-weight: normal;
					padding-right: 0.5rem;
				}

				p {
					font-size: 1.6rem;
				}
			}
		}
	}

	&-related {
		&-heading {
			display: flex;
			flex-flow: row wrap;
			align-items: center;
			width: 100%;
			height: 4.7rem;
			background-color: $color-gray-light-2;
			border: 1px solid $color-gray;
			border-bottom: none;
			// padding: 1.5rem 0.8rem;

			&-info {
				display: flex;
				flex-flow: row nowrap;
				align-items: center;
				font-size: 1.4rem;
				font-weight: 500;
				margin: 0 1.5rem;

				label {
					margin-right: 2rem;
					font-weight: normal;
				}
			}

			.unreceive-all-btn {
				background-color: $color-white;
				border-color: $color-gray;
				text-transform: initial;
			}
		}
	}
}
</style>

<style lang="scss">
.status-badge {
	margin-right: 25px;
	background-color: #fff;
}

.sub-item {
	margin-left: 25px;
	padding-right: 37px;
}

.table-highlight {
	border: 2px solid #c2262f;
	border-radius: 5px;
	scroll-margin: 130px; // clear the fixed app bar + sticky actions bar
}

.po-cancelled {
	position: relative;
	opacity: 0.45;
	pointer-events: none;

	&::after {
		content: "CANCELLED";
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%) rotate(-18deg);
		font-size: 6rem;
		font-weight: 900;
		letter-spacing: 1rem;
		color: rgba(0, 0, 0, 0.08);
		white-space: nowrap;
		pointer-events: none;
		z-index: 1;
	}
}

.to-order {
	margin-left: 8px;
	cursor: pointer;

	&:hover {
		color: #1264f6;
	}
}
</style>
